import { Injectable, isDevMode } from "@angular/core";
import { Action, Selector, State, StateContext, StateToken, Store } from "@ngxs/store";
import { FinOpsOnOffResourcesService } from "./finops-aws-on-off.service";
import {
    LoadFinOpsAwsOnOffHistoricalData,
    LoadFinOpsAwsOnOffChartsData,
    LoadDataTableAllAccountsFinOpsAwsOnOffResources,
    LoadFinOpsAwsOnOffAllAccountsChartsFilteredData
} from "./finops-aws-on-off.actions";
import { finalize, forkJoin } from "rxjs";
import { FinOpsAwsOnOffResource, FinOpsOnOffResourcesStateModel } from "./finops-aws-on-off.model";
import { PreferencesFiltersState } from "src/app/store/preferences/filters/preferences-filters.state";
import { PreferencesFinopsState } from "src/app/store/preferences/finops/preferences-finops.state";
import { PreferencesChartsState } from "src/app/store/preferences/charts/preferences-charts.state";
import { ItemTypeEnum } from "src/app/shared/enum/item-type.enum";
import { DateService, HistoricalDate } from "src/app/shared/service/date.service";
import { DateGranularityEnum } from "src/app/shared/enum/date-granularity.enum";
import { MetaService } from "src/app/store/meta/meta.service";
import { MetaRequestHelper } from "src/app/store/meta/meta-request.helper";
import { DataForDatatableCompute, FinOpsAwsOnOffResourcesHelper } from "./finops-aws-on-off.helper";
import { PreferencesProvidersState } from "src/app/store/preferences/providers/preferences-providers.state";
import { AwsConfig } from "src/app/store/preferences/providers/preferences-providers.model";
import { FinOpsContextState } from "src/app/store/finops/context/finops-context.state";
import { SelectFinOpsDate } from "src/app/store/finops/context/finops-context.actions";

const FINOPS_AWS_ONOFF_TOKEN: StateToken<FinOpsOnOffResourcesStateModel> = new StateToken('finOpsAwsOnOffResources');

const FinOpsOnOffResourcesStateModelDefaults: FinOpsOnOffResourcesStateModel = {
    historicalDataByAccount: {
    },
    datatable: [],
    chartsDataDisplayed: {
        filteredChartsData: [],
    },
    chartsDataByAccount: {},
    filteredCounters: {}
}

@State<FinOpsOnOffResourcesStateModel>({
    name: FINOPS_AWS_ONOFF_TOKEN,
    defaults: FinOpsOnOffResourcesStateModelDefaults
})

@Injectable()
export class FinOpsAwsOnOffResourcesState {

    private readonly KEY = FINOPS_AWS_ONOFF_TOKEN.getName()

    constructor(
        private finOpsAwsOnOffResourcesService: FinOpsOnOffResourcesService,
        private store: Store,
        private dateService: DateService,
        private metaService: MetaService
    ) {
    }

    @Selector([FINOPS_AWS_ONOFF_TOKEN])
    static getHistoricalDataByAccount(state: FinOpsOnOffResourcesStateModel) {
        return state.historicalDataByAccount
    }

    @Selector([FINOPS_AWS_ONOFF_TOKEN])
    static getHistoricalDataByAccountByGranularityByDate(state: FinOpsOnOffResourcesStateModel) {
        return (account: string, granularity: DateGranularityEnum, date: string) => state.historicalDataByAccount[account][granularity][date]
    }

    @Selector([FINOPS_AWS_ONOFF_TOKEN])
    static getDatatable(state: FinOpsOnOffResourcesStateModel) {
        return state.datatable
    }

    @Selector([FINOPS_AWS_ONOFF_TOKEN])
    static getFilteredCounters(state: FinOpsOnOffResourcesStateModel) {
        return state.filteredCounters
    }

    @Selector([FINOPS_AWS_ONOFF_TOKEN])
    static getChartsData(state: FinOpsOnOffResourcesStateModel) {
        return state.chartsDataByAccount
    }

    @Selector([FINOPS_AWS_ONOFF_TOKEN])
    static getChartsDataByAccountByDate(state: FinOpsOnOffResourcesStateModel) {
        return (account: string, date: string) => state.chartsDataByAccount[account][date]
    }

    @Selector([FINOPS_AWS_ONOFF_TOKEN])
    static getFilteredChartsData(state: FinOpsOnOffResourcesStateModel) {
        return state.chartsDataDisplayed.filteredChartsData
    }

    @Action(LoadFinOpsAwsOnOffHistoricalData)
    loadOnOffResourcesHistoricalData(ctx: StateContext<FinOpsOnOffResourcesStateModel>) {
        const finopsConfig = this.store.selectSnapshot(PreferencesFinopsState.getConfig)
        const awsConfig: AwsConfig = this.store.selectSnapshot(PreferencesProvidersState.getAwsConfig)
        const historicalDates: HistoricalDate[] = this.dateService.getFinopsHistoricalDates(finopsConfig)
        let wrapRequestList = []
        let accounts: string[] = []

        awsConfig.accounts.forEach(account => {
            accounts.push(account.account_id)
        });

        for(const account of accounts) {
            for (const historicalDate of historicalDates) {
                const FINOPS_ONOFF_KEY = this.KEY
                + "-account-"
                + account
                + "-granularity-"
                + finopsConfig.granularity as DateGranularityEnum
                + "-date-"
                + historicalDate.date

                if(!this.metaService.isStale(FINOPS_ONOFF_KEY)) {
                    if(isDevMode()) {
                        console.warn("Cache for KEY " + FINOPS_ONOFF_KEY + " still valid, no request executed.")
                    }
                    continue
                }

                wrapRequestList.push(
                    MetaRequestHelper.wrapRequest(
                        this.store,
                        FINOPS_ONOFF_KEY,
                        this.finOpsAwsOnOffResourcesService.loadOnOffResourcesHistoricalData(
                            historicalDate.date, finopsConfig.granularity, account
                        ),
                        onoff_resources => {
                            ctx.setState(state => {
                                const h = {...state.historicalDataByAccount}
                                h[account] = { ...(h[account] || {}) }
                                h[account][finopsConfig.granularity] = {
                                    ...(h[account][finopsConfig.granularity] || {}),
                                    [historicalDate.date]: onoff_resources.measures
                                }
                                return {
                                    ...state,
                                    historicalDataByAccount: h
                                }
                            })
                        }
                    )
                )
            }
        }
        return forkJoin(wrapRequestList).pipe(
            finalize(() => {
                ctx.dispatch(
                    new LoadDataTableAllAccountsFinOpsAwsOnOffResources()
                )
            })
        )
    }

    @Action(LoadDataTableAllAccountsFinOpsAwsOnOffResources)
    loadDataTableAllAccountsFinOpsAwsOnOffResources(ctx: StateContext<FinOpsOnOffResourcesStateModel>) {
        const finopsConfig = this.store.selectSnapshot(PreferencesFinopsState.getConfig)
        const awsConfig: AwsConfig = this.store.selectSnapshot(PreferencesProvidersState.getAwsConfig)
        const granularity = finopsConfig.granularity as DateGranularityEnum;
        const historicalDatesSelected = this.dateService.getFinopsHistoricalDates(finopsConfig).map(item => item.date);
        const selectedDateTile = this.store.selectSnapshot(FinOpsContextState.getSelectedDate)

        // If cannot find selected date in historical dates, select the last one and load data for it
        if (selectedDateTile === undefined || !historicalDatesSelected.includes(selectedDateTile)) {
            ctx.dispatch([
                new SelectFinOpsDate(historicalDatesSelected[historicalDatesSelected.length - 1]),
                new LoadDataTableAllAccountsFinOpsAwsOnOffResources()
            ])
            return
        }

        if (isDevMode()) {
            console.warn("Loading all accounts data for granularity " + granularity + " and date " + selectedDateTile)
        }

        const filters = this.store.selectSnapshot(PreferencesFiltersState.getFiltersByType)[ItemTypeEnum[ItemTypeEnum.FINOPS_AWS_ON_OFF_RESOURCES]];

        let total: Record<string, DataForDatatableCompute> = {};

        for (const account of awsConfig.accounts) {
            const historicalDataByGranularity = ctx.getState().historicalDataByAccount?.[account.account_id]?.[granularity];
            if (!historicalDataByGranularity) continue;

            total[account.account_id] = FinOpsAwsOnOffResourcesHelper.computeDataTable(
                historicalDatesSelected,
                historicalDataByGranularity,
                filters
            );
        }

        const grandTotal = FinOpsAwsOnOffResourcesHelper.aggregateDataTables(
            historicalDatesSelected,
            total
        );

        ctx.patchState({
            datatable: grandTotal.measuresDatatable[selectedDateTile],
            filteredCounters: grandTotal.filteredCounters
        });
    }

    @Action(LoadFinOpsAwsOnOffChartsData)
    loadFinOpsAwsOnOffChartsData(ctx: StateContext<FinOpsOnOffResourcesStateModel>) {
        const chartsConfig = this.store.selectSnapshot(PreferencesChartsState.getConfig)
        const awsConfig: AwsConfig = this.store.selectSnapshot(PreferencesProvidersState.getAwsConfig)
        let accounts: string[] = []
        let wrapRequestList = []
        const stringDate = DateService.formatShortDate(DateService.resolveChartsDate(chartsConfig))

        awsConfig.accounts.forEach(account => {
            accounts.push(account.account_id)
        });

        for(const account of accounts) {
            const FINOPS_ONOFF_CHARTS_DATE_KEY = this.KEY
            + '-charts-'
            + 'account-' + account
            + '-date-' + DateService.formatShortDate(DateService.resolveChartsDate(chartsConfig))

            if(!this.metaService.isStale(FINOPS_ONOFF_CHARTS_DATE_KEY)) {
                if(isDevMode()) {
                    console.warn("Cache for KEY " + FINOPS_ONOFF_CHARTS_DATE_KEY + " still valid, no request executed.")
                }
                continue
            }

            wrapRequestList.push(
                MetaRequestHelper.wrapRequest(
                    this.store,
                    FINOPS_ONOFF_CHARTS_DATE_KEY,
                    this.finOpsAwsOnOffResourcesService.loadOnOffResourcesMeasures(stringDate, account),
                    (onoff_charts) => {
                        ctx.setState(state => {
                            const shallowCloneAllChartsData = {...state.chartsDataByAccount}
                            shallowCloneAllChartsData[account] = { ...(shallowCloneAllChartsData[account] || {}) }
                            shallowCloneAllChartsData[account][stringDate] = onoff_charts.measures
                            return {
                                ...state,
                                chartsDataByAccount: shallowCloneAllChartsData
                            }
                        })
                    }
                )
            )
        }

        return forkJoin(wrapRequestList).pipe(
            finalize( () => {
                ctx.dispatch(
                    new LoadFinOpsAwsOnOffAllAccountsChartsFilteredData()
                )
            })
        )
    }

    @Action(LoadFinOpsAwsOnOffAllAccountsChartsFilteredData)
    loadFinOpsAwsOnOffAllAccountsChartsFilteredData(ctx: StateContext<FinOpsOnOffResourcesStateModel>) {
        const chartsConfig = this.store.selectSnapshot(PreferencesChartsState.getConfig)
        const awsConfig: AwsConfig = this.store.selectSnapshot(PreferencesProvidersState.getAwsConfig)
        const bulkChartsData = ctx.getState().chartsDataByAccount

        const filters = this.store.selectSnapshot(PreferencesFiltersState.getFiltersByType)[ItemTypeEnum[ItemTypeEnum.FINOPS_AWS_ON_OFF_RESOURCES_CHARTS]]

        const stringDate = DateService.formatShortDate(DateService.resolveChartsDate(chartsConfig))
        
        let onOffAllCharts: FinOpsAwsOnOffResource[] = []

        for(const account of awsConfig.accounts) {
            let charts = FinOpsAwsOnOffResourcesHelper.computeOnOffChartData(
                bulkChartsData[account.account_id],
                stringDate,
                filters
            )
            charts.forEach(chart => onOffAllCharts.push(chart))
        }

        ctx.patchState(
            {
                chartsDataDisplayed: {
                    filteredChartsData: onOffAllCharts
                }
            }
        )

    }

}
